import { useCallback } from 'react';
import {
  TableWithHeader,
  TableWithHeaderProps,
  TableHeaderVariant,
} from './shared/TableWithHeader';
import {
  DependenciesContentViewType,
  SelectedDependencyWithClearHistoryProps,
} from '../dependencies/types';
import { ConfigurationType } from '@server/parser';
import { TableTypography } from '../../common/table/utils';
import { humanizeDate } from '../../helpers/humanizeDate';
import groupBy from 'lodash/groupBy';
import { DataTableRow } from '../../common/table/TableTypes';
import { useSelector } from 'react-redux';
import { selectSaltoObjectTypes } from '../../../reducers/global/globalReducer';
import { TableItemHighlightedText } from '../dependencies/search/highlight/TableItemHighlightedText';
import { PossibleSectionInDependencies } from '../dependencies/search/types';

interface CpqTablesItem {
  id: string;
  name: string;
  lastModified: string;
  label: string;
  type: string;
  objectName?: string;
}

interface CpqTablesProps extends Pick<TableWithHeaderProps, 'headerVariant'> {
  items: CpqTablesItem[];
  crmOrgId: string;
  clearHistory: boolean;
  onItemClick: (props: SelectedDependencyWithClearHistoryProps) => void;
  activeItemId?: string;
  section?: PossibleSectionInDependencies;
}

const columns = [
  { field: 'label', headerName: 'Name', width: '215px' },
  { field: 'lastModified', headerName: 'Last Modified' },
];

const renderRows = ({
  items,
  activeItemId,
  section = 'dependencies',
}: {
  items: CpqTablesItem[];
  activeItemId?: string;
  section?: PossibleSectionInDependencies;
}) => {
  return items.map((row, index) => ({
    id: row.id,
    name: row.name,
    objectName: row.objectName,
    label: (
      <TableTypography>
        <TableItemHighlightedText
          text={row.label}
          field="label"
          section={section}
          tableType="cpqData"
          rowIndex={index}
        />
      </TableTypography>
    ),
    lastModified: (
      <TableTypography>
        <TableItemHighlightedText
          text={humanizeDate({ dateOrTimestamp: row.lastModified })}
          field="lastModified"
          section={section}
          tableType="cpqData"
          rowIndex={index}
        />
      </TableTypography>
    ),
    isRowActive: activeItemId === row.id || activeItemId === row.name,
    isClickable: true,
  }));
};

export const CpqTables = ({
  items,
  crmOrgId,
  clearHistory,
  onItemClick,
  activeItemId,
  section = 'dependencies',
  ...props
}: CpqTablesProps) => {
  const saltoObjectTypes = useSelector(selectSaltoObjectTypes(crmOrgId));
  const cpqMap = groupBy(items, 'objectName');

  const onRowClick = useCallback(
    (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>, row: DataTableRow) => {
      const objectType = saltoObjectTypes?.find((type) => type.objectType === row.objectName);
      onItemClick({
        id: row.id,
        name: row.name,
        parentType: ConfigurationType.cpqData,
        dependencyType: objectType?.label ?? row.objectName,
        objectName: row.objectName,
        clearHistory,
        contentType: DependenciesContentViewType.default,
      });
    },
    [onItemClick, clearHistory, saltoObjectTypes],
  );

  return (
    <>
      {Object.keys(cpqMap).map((objectName) => {
        const objectType = saltoObjectTypes?.find((type) => type.objectType === objectName);
        const tableHeader = objectType?.label ?? objectName;
        const _items = renderRows({
          items: cpqMap?.[objectName] ?? [],
          activeItemId,
          section,
        });
        return (
          <TableWithHeader
            {...props}
            key={`tableHeader_${tableHeader}`}
            tableHeader={tableHeader}
            columns={columns}
            rows={_items}
            onRowClick={onRowClick}
            headerVariant={TableHeaderVariant.expandable}
            dataTableType="cpqData"
            section={section}
          />
        );
      })}
    </>
  );
};
